import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, User, Home, BookOpen, Settings } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Forms', icon: Home },
    { path: '/knowledge', label: 'Shared Knowledge', icon: BookOpen },
  ];

  return (
    <header className="bg-card border-b border-border sticky top-0 z-40">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-8">
            <h1 className="text-xl font-bold text-foreground">Rhety Forms</h1>
            <div className="hidden md:flex">
              {user && (
                <nav className="flex space-x-6 items-center">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center space-x-3 px-3 py-2 text-base md:text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon className="w-5 h-5 md:w-4 md:h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
              )}
            </div>
          </div>
          
          {/* Desktop View */}
          <div className="hidden md:flex items-center space-x-4">
            {user && (
              <>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>Welcome, {user.username}</span>
                  {user.is_superuser && (
                    <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full dark:bg-purple-900/20 dark:text-purple-400">
                      Platform Admin
                    </span>
                  )}
                </div>
                {user.is_superuser && (
                  <Link
                    to="/settings"
                    className={`p-2 rounded-md transition-colors ${
                      location.pathname === '/settings'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    title="Settings"
                  >
                    <Settings className="w-4 h-4" />
                  </Link>
                )}
                <ThemeToggle />
                <LanguageSwitcher />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </Button>
              </>
            )}
          </div>

          {/* Mobile View - keeping simple for now, can add Sheet component later */}
          <div className="md:hidden flex items-center space-x-2">
            {user && user.is_superuser && (
              <Link
                to="/settings"
                className={`p-2 rounded-md transition-colors ${
                  location.pathname === '/settings'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title="Settings"
              >
                <Settings className="w-4 h-4" />
              </Link>
            )}
            {user && <ThemeToggle />}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;